import { useQuery } from '@tanstack/react-query'
import { nativeSportsbookApi } from '@/services/api'
import { collectionChoices } from './collectionModel'
import { PLAYER_PROP_LEAGUES } from './playerPropBoardModel'
import { isLiveSource } from './catalogSources'

export const leagueKey = (sport, competition) => `${sport}|${competition}`

// Discovery follows the preview's source: the tenant runtime under live data,
// the sample boards (and provider templates) otherwise.
export function usePlayerPropMarkets({ slug, catalog, sport, competition, eventId }) {
  const source = catalog?.source
  return useQuery({
    queryKey: ['native-player-prop-markets', slug, source, sport, competition, eventId || ''],
    enabled: isLiveSource(source) && Boolean(slug && sport && competition),
    queryFn: ({ signal }) => nativeSportsbookApi.playerPropMarkets(slug, { sport, competition, eventId, source }, signal),
    staleTime: 30000, retry: false,
  })
}

export function discoveryNote(live, discovery) {
  if (!live) return 'Sample data answers the provider templates. Rehearse with live data to pick from every posted market.'
  if (discovery.isFetching && !discovery.data) return 'Reading the markets this league posts…'
  if (discovery.isError) return discovery.error?.response?.status === 404 ? 'The provider does not offer this league.' : 'Posted markets are unavailable. Retry shortly; templates still apply.'
  if (discovery.data?.state === 'empty') return 'The provider posts no player props for these games yet.'
  if (discovery.data?.state === 'unavailable') return 'Posted markets are unavailable. Retry shortly; templates still apply.'
  return null
}

/** The leagues a picker offers: the observed template leagues, the current inventory's, and the current one. */
export function leagueChoices(catalog, scope, extra = []) {
  const known = collectionChoices(catalog?.events || []).competition_scopes.map((choice) => ({ sport: choice.sport, competition: choice.name }))
  const leagues = new Map([...PLAYER_PROP_LEAGUES, ...extra, ...known].map((league) => [leagueKey(league.sport, league.competition), league]))
  if (scope.sport && scope.competition && !leagues.has(leagueKey(scope.sport, scope.competition))) leagues.set(leagueKey(scope.sport, scope.competition), { sport: scope.sport, competition: scope.competition })
  return [...leagues.values()]
}

/** What a builder league's game-line discovery is doing, so a failed read never looks like no markets. */
export function linesDiscoveryNote(live, discovery) {
  if (!live) return null
  if (discovery.isFetching && !discovery.data) return 'Reading the game lines this league posts…'
  if (discovery.isError) return discovery.error?.response?.status === 404 ? 'The provider does not offer this league.' : 'Posted game lines are unavailable. Retry shortly; templates still apply.'
  if (discovery.data?.state === 'empty') return 'The provider posts no same game parlay lines for these games yet.'
  if (discovery.data?.state === 'unavailable') return 'Posted game lines are unavailable. Retry shortly; templates still apply.'
  return null
}

// Game-line discovery for a builder league follows the same source rule.
export function useParlayBuilderLines({ slug, catalog, sport, competition }) {
  const source = catalog?.source
  return useQuery({
    queryKey: ['native-parlay-builder-lines', slug, source, sport, competition],
    enabled: isLiveSource(source) && Boolean(slug && sport && competition),
    queryFn: ({ signal }) => nativeSportsbookApi.parlayBuilderLines(slug, { sport, competition, source }, signal),
    staleTime: 30000, retry: false,
  })
}
